import { parseISO } from 'date-fns/parseISO';

import * as constants from '../../../helpers/constants/dataTypeConstants';
import gridEntryHelper from '../../../helpers/data-parsing/gridEntryHelper';
import dateValidationDispatcher from '../../../helpers/dateValidationDispatcher';
import {
  getPreferDirects,
  isPreferDirectsEnabled,
  isPreferDirectsOverridden,
} from '../../../selectors';

import {
  receivePriceTraces,
  receiveMonthDataAnalytics,
  receiveMonthDataStarted,
} from './monthViewFlightDataActions';

import type {
  RawMonthDataResponse,
  RawMonthResponse,
} from '../../../actions/types/dataTypes';
import type { MonthGridData, State } from '../../../selectors/types';
import type { Dispatch } from 'redux';

// --- Response parsing ---
export const getMonthDataPayload = (json: RawMonthDataResponse) =>
  json.PriceGrids.Grid;

export const getMonthDataMetaData = (json: RawMonthDataResponse) =>
  json.Metadata || {};

export const getMonthPriceTraces = (json: RawMonthDataResponse) => json.Traces;

const getOutboundMonth = (json: RawMonthResponse) => json.Outbound;
const getInboundMonth = (json: RawMonthResponse) => json.Inbound;

export const parseWholeYearDates = (json: RawMonthResponse) => {
  const rawOutboundDate = getOutboundMonth(json) || 'Invalid Date';
  const outboundDate = parseISO(rawOutboundDate);
  const rawInboundDate = getInboundMonth(json);
  const inboundDate = rawInboundDate ? parseISO(rawInboundDate) : undefined;

  return { outboundDate, inboundDate, isReturn: !!inboundDate };
};

// --- Process received grid data ---
interface ProcessReceivedMonthDataOptions {
  grid: MonthGridData | null;
  url: string;
  callMetadata: Record<string, unknown>;
  enableCheapestDates: boolean | string | number;
  currentOutboundMonth: Date | null;
  dispatch: Dispatch;
  getState: () => State;
  toggleFilter: (checked: boolean, source: string) => void;
  setFilterEnabled: (enabled: boolean) => void;
}

export const processReceivedMonthData = ({
  callMetadata,
  currentOutboundMonth,
  dispatch,
  enableCheapestDates,
  getState,
  grid,
  setFilterEnabled,
  toggleFilter,
  url,
}: ProcessReceivedMonthDataOptions) => {
  const payload = {
    grid,
    url,
    ...(enableCheapestDates && { outboundMonth: currentOutboundMonth }),
  };

  dispatch(receiveMonthDataStarted(payload, callMetadata));

  const hasDirects = gridEntryHelper.hasDirectFlight(grid);
  const preferDirects = getPreferDirects(getState());
  const preferDirectsOverridden = isPreferDirectsOverridden(getState());
  const preferDirectsEnabled = isPreferDirectsEnabled(getState());

  if (hasDirects) {
    if (!preferDirectsEnabled && !preferDirects && preferDirectsOverridden) {
      toggleFilter(true, constants.AUTOMATED_SOURCE);
    }
  } else {
    toggleFilter(false, constants.AUTOMATED_SOURCE);
  }

  if (hasDirects !== preferDirectsEnabled) {
    setFilterEnabled(hasDirects);
  }

  dateValidationDispatcher.ensureValidDates(getState(), dispatch);
  dispatch(receiveMonthDataAnalytics(callMetadata));
};

// --- Dispatch price traces from response ---
export const dispatchPriceTraces = (
  json: RawMonthDataResponse,
  dispatch: Dispatch,
) => {
  dispatch(receivePriceTraces(getMonthPriceTraces(json)));
};
